import { type ReactNode, type Ref, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { XMarkIcon } from '@heroicons/react/24/solid';
import { useRouteLoaderData } from '@remix-run/react';
import { ClientOnly } from 'remix-utils/client-only';

import type { PwaPromptBranding } from '~/components/PWA/InstallPrompt';
import { isAndroidDevice } from '~/components/PWA/isAndroidDevice';
import { PWA_PROMPT_STORAGE_KEYS } from '~/components/PWA/pwaPromptConfig';
import { useDeferredInstallPrompt } from '~/components/PWA/useDeferredInstallPrompt';
import { useIsMobileDevice } from '~/components/PWA/useIsMobileDevice';
import { usePromptDismissal } from '~/components/PWA/usePromptDismissal';
import { usePwaInstalled } from '~/components/PWA/usePwaInstalled';
import { showInstallPrompt } from '~/components/PWA/utils';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import { useNectarImpression } from '~/hooks/useNectarImpression';
import type { loader as communityLoader } from '~/routes/community';
import { communityImageSrc } from '~/routes/community/_utils/communityImageSrc';
import { NectarPageBlock, nectarPageBlock } from '~/utils/nectarPageBlock';

import { PillButton } from './ui/PillButton';

import { useCommunityMembership } from '../_hooks/useCommunityMembership';
import { useDismissMobileAppCalloutMutation } from '../_hooks/useDismissMobileAppCalloutMutation';
import { CommunityMobileAppQrDialog } from './CommunityMobileAppQrDialog';

const THIRTY_MINUTES_MS = 30 * 60 * 1000;

type InstallAppCalloutProps = {
  branding: PwaPromptBranding;
};

const InstallAppCallout = ({ branding }: InstallAppCalloutProps) => {
  return <ClientOnly>{() => <InstallAppCalloutImpl branding={branding} />}</ClientOnly>;
};

// Shared eligibility wrapper: logged-in member with branding. Each device then owns its own
// dismissal model (mobile: local storage; desktop: backend flag + 30-minute timed local dismissal).
function InstallAppCalloutImpl({ branding }: InstallAppCalloutProps) {
  const isLoggedIn = useIsLoggedIn();
  const { isMember } = useCommunityMembership();
  const isMobile = useIsMobileDevice();

  if (!isLoggedIn || !isMember) return null;
  if (!branding.logoUrl || !branding.name) return null;

  return isMobile ? <MobileInstallCallout branding={branding} /> : <DesktopInstallCallout branding={branding} />;
}

function MobileInstallCallout({ branding }: { branding: PwaPromptBranding }) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = usePromptDismissal(PWA_PROMPT_STORAGE_KEYS.communityInstallCallout);
  const { isPwaInstalled, markPwaInstalled } = usePwaInstalled(PWA_PROMPT_STORAGE_KEYS.communityInstalled);
  const isAndroid = isAndroidDevice();
  const deferredPrompt = useDeferredInstallPrompt();

  if (dismissed || isPwaInstalled) return null;

  const onInstall = async () => {
    const prompt = window.__DeferredPrompt__ ?? deferredPrompt;
    if (!prompt) return;

    await prompt.prompt();
    const choice = await prompt.userChoice;
    if (choice.outcome === 'accepted') {
      markPwaInstalled();
    }
  };

  return (
    <CalloutCard
      branding={branding}
      onDismiss={() => setDismissed(true)}
      heading={t('pwa.install_prompt.install_prompt')}
      description={t('pwa.install_callout.description')}
    >
      {isAndroid ? (
        <PillButton type="button" variant="primary" onClick={onInstall} disabled={!deferredPrompt} fullWidth>
          {t('pwa.install_prompt.install_cta')}
        </PillButton>
      ) : (
        <PillButton type="button" variant="primary" onClick={() => showInstallPrompt()} fullWidth>
          {t('pwa.install_callout.ios_cta')}
        </PillButton>
      )}
    </CalloutCard>
  );
}

// Ignores desktop-PWA install state: a desktop install does not prove the phone has the app.
function DesktopInstallCallout({ branding }: { branding: PwaPromptBranding }) {
  const { t } = useTranslation();
  const { currentMember } = useCommunityMembership();
  const layoutData = useRouteLoaderData<typeof communityLoader>('routes/community');
  const enabled = Boolean(layoutData?.community?.mobile_app_callout_enabled);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [remindedLater, setRemindedLater] = usePromptDismissal(
    PWA_PROMPT_STORAGE_KEYS.communityInstallCalloutVisit,
    THIRTY_MINUTES_MS,
  );
  const { mutate: dismissForever, isPending } = useDismissMobileAppCalloutMutation();
  const impressionRef = useNectarImpression(
    { pageBlock: NectarPageBlock.CommunityMobileAppCallout },
    { fireWhenInViewport: true, enabled },
  );

  const permanentlyDismissed = currentMember?.mobile_app_callout_dismissed ?? true;
  if (!enabled || permanentlyDismissed || remindedLater) return null;

  return (
    <CalloutCard
      ref={impressionRef}
      branding={branding}
      heading={t('community.mobile_app.desktop_heading', { communityName: branding.name })}
      pageBlock={NectarPageBlock.CommunityMobileAppCallout}
    >
      <PillButton type="button" variant="primary" fullWidth onClick={() => setDialogOpen(true)}>
        {t('community.mobile_app.show_qr_cta')}
      </PillButton>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <PillButton
          type="button"
          variant="secondary"
          fullWidth
          disabled={isPending}
          onClick={() => setRemindedLater(true)}
        >
          {t('community.mobile_app.remind_later')}
        </PillButton>
        <PillButton type="button" variant="secondary" fullWidth disabled={isPending} onClick={() => dismissForever()}>
          {t('community.mobile_app.dont_show_again')}
        </PillButton>
      </div>
      <CommunityMobileAppQrDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        pageBlock={NectarPageBlock.CommunityMobileAppCallout}
      />
    </CalloutCard>
  );
}

function CalloutCard({
  branding,
  onDismiss,
  children,
  heading,
  description,
  pageBlock,
  ref,
}: {
  branding: PwaPromptBranding;
  onDismiss?: () => void;
  children: ReactNode;
  heading: string;
  description?: string;
  pageBlock?: NectarPageBlock;
  ref?: Ref<HTMLElement>;
}) {
  return (
    <article
      ref={ref}
      className="w-full rounded-3xl border border-black/10"
      {...(pageBlock ? nectarPageBlock(pageBlock) : {})}
    >
      <div className="flex items-start gap-4 p-4">
        <img
          src={communityImageSrc(branding.logoUrl, 128)}
          alt={branding.name}
          className="size-12 shrink-0 overflow-clip rounded-xl"
        />
        <div className="min-w-0 flex-1">
          <h2 className="break-words text-base font-semibold leading-5 tracking-[-0.16px]">{heading}</h2>
          {description ? <p className="mt-1 text-sm leading-5 text-black/60">{description}</p> : null}
        </div>
        {onDismiss ? (
          <button type="button" aria-label="Close" className="shrink-0" onClick={onDismiss}>
            <XMarkIcon className="size-5 text-black/40" />
          </button>
        ) : null}
      </div>
      <div className="px-4 pb-4">{children}</div>
    </article>
  );
}

export default InstallAppCallout;
